<template>
  <div class="h-full w-full flex flex-col min-h-screen">
    <div class="font-[sans-serif] text-ink flex-1 flex flex-col">
      <div class="flex-1 flex flex-col items-center pt-[80px] -justify-center py-6 px-4">
        <div class="max-w-md w-full flex justify-end mb-3"><ThemeToggle /></div>
        <div class="max-w-md w-full border py-8 px-6 rounded shadow-lg border-line bg-surface">
          <div class="flex justify-center">
            <img
                src="https://www.daytradercanada.com/wp-content/themes/daytrader/public/images/svg/logo.svg"
                alt="logo"
                class="w-40 mb-10 dark:hidden"
            />
            <img src="../assets/logo-daytrader-dark.svg" alt="logo" class="w-40 mb-10 hidden dark:block" />
          </div>

          <div class="text-left text-[1.4rem] text-ink font-semibold">
            <!-- Authentication Required -->
            {{ $t('login-heading') }}
          </div>
          <span class="text-[0.8rem] text-muted">
            <!-- Please authenticate your account to access DTC screener. -->
            {{ $t('login-subheading') }}
          </span>
          <form :class="isLoading ? 'pointer-events-none' : ''" class="mt-10 space-y-4">
            <div class="relative flex items-center">
              <input name="email" v-model="email" type="email"
                class="w-full text-sm px-4 py-3 rounded outline-none border-2 bg-surface focus:border-[#009ee2]"
                :class="emailError ? 'border-red-500' : 'border-line'" :placeholder="$t('emil-palceholder')" />
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"
                class="text-faint w-[18px] h-[18px] absolute right-4 cursor-pointer">
                <path stroke-linecap="round" stroke-linejoin="round"
                  d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
              </svg>
            </div>

            <div class="relative flex items-center">
              <input name="password" v-model="password" @keyup.enter="login" :type="showPassword ? 'text' : 'password'"
                class="w-full text-sm px-4 py-3 rounded outline-none border-2 bg-surface focus:border-[#009ee2]"
                :class="passwordError ? 'border-red-500' : 'border-line'" :placeholder="$t('password-placeholder')" />
              <svg v-if="showPassword" @click="showPassword = !showPassword" xmlns="http://www.w3.org/2000/svg"
                fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"
                class="text-faint w-[18px] h-[18px] absolute right-4 cursor-pointer">
                <path stroke-linecap="round" stroke-linejoin="round"
                  d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88" />
              </svg>
              <svg v-else @click="showPassword = !showPassword" xmlns="http://www.w3.org/2000/svg" fill="currentColor"
                stroke="currentColor" class="text-faint w-[18px] h-[18px] absolute right-4 cursor-pointer" viewBox="0 0 128 128">
                <path
                  d="M64 104C22.127 104 1.367 67.496.504 65.943a4 4 0 0 1 0-3.887C1.367 60.504 22.127 24 64 24s62.633 36.504 63.496 38.057a4 4 0 0 1 0 3.887C126.633 67.496 105.873 104 64 104zM8.707 63.994C13.465 71.205 32.146 96 64 96c31.955 0 50.553-24.775 55.293-31.994C114.535 56.795 95.854 32 64 32 32.045 32 13.447 56.775 8.707 63.994zM64 88c-13.234 0-24-10.766-24-24s10.766-24 24-24 24 10.766 24 24-10.766 24-24 24zm0-40c-8.822 0-16 7.178-16 16s7.178 16 16 16 16-7.178 16-16-7.178-16-16-16z"
                  data-original="#000000" />
              </svg>
            </div>

            <div class="!mt-10">
              <div v-if="error" class="text-red-500 text-[14px] mb-1">
                {{ $t('login-error') }}
              </div>
              <button type="button" @click="login"
                :class="isLoading ? 'bg-line-strong pointer-events-none' : 'bg-brand hover:bg-brand-hover'"
                class="w-full py-2.5 px-4 text-sm rounded-[6px] flex items-center justify-center space-x-2 text-white focus:outline-none">
                <span v-if="isLoading">
                  <svg aria-hidden="true" class="w-4 h-4 text-line animate-spin fill-brand"
                    viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                      fill="currentColor" />
                    <path
                      d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                      fill="currentFill" />
                  </svg>
                </span>
                <span>{{ $t('Login') }}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
      <AppFooter />
    </div>
  </div>
</template>

<script>
import axios from '../api/axios.js';
import jwtDecode from 'jwt-decode';
import AppFooter from '../components/Footer.vue';
import ThemeToggle from '../components/ThemeToggle.vue';

export default {
  name: 'LoginPage',
  components: {
    AppFooter,
    ThemeToggle,
  },
  data() {
    return {
      email: '',
      password: '',
      showPassword: false,
      isLoading: false,
      emailError: false,
      passwordError: false,
      error: '',
    };
  },
  methods: {
    async login() {
      const regex = /^\s*$/;
      if (regex.test(this.email)) {
        this.emailError = true;
        return;
      }
      if (regex.test(this.password)) {
        this.passwordError = true;
        return;
      }

      const payload = {
        email: this.email,
        password: this.password,
      };

      this.isLoading = true;
      try {
        const res = await axios.post('api/LearnWorldAuth/login', payload);
        if (res?.data?.ssoResponse?.status) {
          // Save tokens and email to localStorage
          localStorage.setItem('email', this.email);
          localStorage.setItem('token', res?.data?.ssoResponse?.access_token);
          localStorage.setItem('jwtToken', res?.data?.jwtToken);
          // Redirect to protected route
          this.$router.push('/screener');
        } else {
          this.error = res?.data?.message;
        }
      } catch (err) {
        this.error = 'Invalid login credentials';
      }
      this.isLoading = false;
    },
  },
  watch: {
    email() {
      this.emailError = false;
    },
    password() {
      this.passwordError = false;
    },
    error() {
      setTimeout(() => {
        this.error = '';
      }, 3000);
    },
  },
  mounted() {
    // On component mount, check if a token exists and if it is still valid.
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        const currentTime = Date.now() / 1000; // current time in seconds
        if (decoded.exp > currentTime) {
          // Token is valid, redirect to protected route
          this.$router.push('/screener');
        } else {
          // Token expired, remove it.
          localStorage.removeItem('token');
        }
      } catch (e) {
        // Token invalid or corrupted; remove it.
        localStorage.removeItem('token');
      }
    }
  },
};
</script>

<style></style>